import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchReportHistory, type ReportHistoryItem } from '../../api/client';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function ReportHistory() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReportHistory()
      .then(setReports)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        Loading reports...
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="py-16 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        </div>
        <p className="text-sm font-medium text-foreground">No reports yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Run your first analysis to see results here.
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {reports.map((report) => (
        <button
          key={report.chat_id}
          type="button"
          className="flex w-full cursor-pointer items-center gap-4 px-2 py-4 text-left transition-colors hover:bg-muted/50"
          onClick={() => navigate(`/report/${report.chat_id}`)}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary/10">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-secondary">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-sm font-medium text-foreground">
              {report.project_name || 'Business Audit'}
            </span>
            <span className="ml-2 text-xs text-muted-foreground">
              {formatDate(report.created_at)}
              {report.file_count > 0 && ` · ${report.file_count} file${report.file_count !== 1 ? 's' : ''}`}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}
